/**
 * Shared editorial content configuration.
 *
 * Single source of truth for editorial cards shown on homepage (EditorialGrid)
 * and featured items in taxonomy dropdown menus.
 */

import { editorialCategoryToToken } from "@/utils/genderMapping";

export interface EditorialCard {
  image: string;
  label: string;
}

export interface LaneContent {
  cards: EditorialCard[];
  shopAllLabel: string;
  shopAllTaxonomy: string;
}

export const EDITORIAL_LANE = {
  WOMENSWEAR: "Womenswear",
  MENSWEAR: "Menswear",
  KIDSWEAR: "Kidswear",
  BEAUTY: "Beauty",
} as const;

export type EditorialLane = (typeof EDITORIAL_LANE)[keyof typeof EDITORIAL_LANE];

const EDITORIAL_LANES = [
  EDITORIAL_LANE.WOMENSWEAR,
  EDITORIAL_LANE.MENSWEAR,
  EDITORIAL_LANE.KIDSWEAR,
  EDITORIAL_LANE.BEAUTY,
] as const;

function isEditorialLane(value: string): value is EditorialLane {
  return EDITORIAL_LANES.includes(value as EditorialLane);
}

function getActiveEditorialCards(
  cardLibrary: Record<EditorialLane, EditorialCard[]>,
  activeIndices: Record<EditorialLane, number[]>,
  lane: EditorialLane
): EditorialCard[] {
  const cards = cardLibrary[lane] ?? [];
  const indices = activeIndices[lane] ?? [];

  return indices
    .map((index) => cards[index])
    .filter((card): card is EditorialCard => Boolean(card));
}

export const EDITORIAL_CARD_LIBRARY_BY_LANE: Record<EditorialLane, EditorialCard[]> = {
  [EDITORIAL_LANE.WOMENSWEAR]: [
    { image: "/fall_sweater.png", label: "Fall knitwear & sweaters" },
    { image: "/women_boots.png", label: "Women's Boots" },
    { image: "/baggy_denim.png", label: "Baggy dark wash denim" },
    { image: "/women_mules_loafers.png", label: "Women's mules & loafers" },
    { image: "/women_leather_flats.png", label: "Leather flats" },
    { image: "/women_poplin_dresses_spring.avif", label: "Poplin dresses for spring" },
  ],
  [EDITORIAL_LANE.MENSWEAR]: [
    { image: "/winter_jacket.png", label: "Spring jackets" },
    { image: "/white_sneakers.png", label: "Every day white sneakers" },
    { image: "/relaxed_trousers.png", label: "Relaxed trousers & pleated pants" },
  ],
  [EDITORIAL_LANE.KIDSWEAR]: [
    { image: "/kids_boots.png", label: "Rain boots & puddle-ready shoes" },
    { image: "/kids_coats.png", label: "Warm winter coats & parkas" },
    { image: "/kids_pjs.png", label: "Pajamas & loungewear" },
    { image: "/kids_coats.png", label: "Windbreakers & light layers" },
  ],
  [EDITORIAL_LANE.BEAUTY]: [
    { image: "/beauty_skincare.png", label: "Skincare essentials" },
    { image: "/beauty_fragrance.png", label: "Fragrance & perfumes" },
    { image: "/beauty_tools.png", label: "Beauty tools & accessories" },
  ],
};

// Change these indices to choose which saved card options appear on the landing page.
export const ACTIVE_EDITORIAL_CARD_INDICES_BY_LANE: Record<EditorialLane, number[]> = {
  [EDITORIAL_LANE.WOMENSWEAR]: [2, 3, 5],
  [EDITORIAL_LANE.MENSWEAR]: [0, 1, 2],
  [EDITORIAL_LANE.KIDSWEAR]: [0, 3, 2],
  [EDITORIAL_LANE.BEAUTY]: [0, 1, 2],
};

/**
 * Editorial content for each shopping lane.
 * Used by EditorialGrid on homepage and referenced by Featured sections.
 */
export const EDITORIAL_CONTENT_BY_LANE: Record<EditorialLane, LaneContent> = {
  [EDITORIAL_LANE.WOMENSWEAR]: {
    cards: getActiveEditorialCards(
      EDITORIAL_CARD_LIBRARY_BY_LANE,
      ACTIVE_EDITORIAL_CARD_INDICES_BY_LANE,
      EDITORIAL_LANE.WOMENSWEAR
    ),
    shopAllLabel: "Shop all women's",
    shopAllTaxonomy: EDITORIAL_LANE.WOMENSWEAR,
  },
  [EDITORIAL_LANE.MENSWEAR]: {
    cards: getActiveEditorialCards(
      EDITORIAL_CARD_LIBRARY_BY_LANE,
      ACTIVE_EDITORIAL_CARD_INDICES_BY_LANE,
      EDITORIAL_LANE.MENSWEAR
    ),
    shopAllLabel: "Shop all men's",
    shopAllTaxonomy: EDITORIAL_LANE.MENSWEAR,
  },
  [EDITORIAL_LANE.KIDSWEAR]: {
    cards: getActiveEditorialCards(
      EDITORIAL_CARD_LIBRARY_BY_LANE,
      ACTIVE_EDITORIAL_CARD_INDICES_BY_LANE,
      EDITORIAL_LANE.KIDSWEAR
    ),
    shopAllLabel: "Shop all kids",
    shopAllTaxonomy: EDITORIAL_LANE.KIDSWEAR,
  },
  [EDITORIAL_LANE.BEAUTY]: {
    cards: getActiveEditorialCards(
      EDITORIAL_CARD_LIBRARY_BY_LANE,
      ACTIVE_EDITORIAL_CARD_INDICES_BY_LANE,
      EDITORIAL_LANE.BEAUTY
    ),
    shopAllLabel: "Shop all beauty",
    shopAllTaxonomy: EDITORIAL_LANE.BEAUTY,
  },
};

/**
 * Which card index to feature in taxonomy dropdown.
 * Set to a number to use that card from EDITORIAL_CONTENT_BY_LANE.
 * Or use FEATURED_OVERRIDES for custom content not in the editorial grid.
 */
export const FEATURED_CARD_INDEX: Partial<Record<EditorialLane, number>> = {
  [EDITORIAL_LANE.WOMENSWEAR]: 1, // "Women's mules & loafers"
  [EDITORIAL_LANE.MENSWEAR]: 0, // "Spring jackets"
  [EDITORIAL_LANE.KIDSWEAR]: 0, // "Rain boots & puddle-ready shoes"
  // Beauty uses override instead
};

/**
 * Custom overrides for featured items when you want something
 * different from the editorial grid cards.
 */
export const FEATURED_OVERRIDES: Partial<Record<EditorialLane, EditorialCard>> = {
  // Beauty uses a custom "Clean beauty essentials" instead of editorial grid cards
  [EDITORIAL_LANE.BEAUTY]: {
    image: "/beauty_clean.png",
    label: "Clean beauty essentials",
  },
};

/**
 * Get the featured editorial item for a lane.
 * Returns override if defined, otherwise the card at FEATURED_CARD_INDEX.
 * Returns undefined if the item is missing required image or label.
 */
export function getFeaturedEditorialItem(lane: string): EditorialCard | undefined {
  if (!isEditorialLane(lane)) {
    return undefined;
  }

  let item: EditorialCard | undefined;

  // Check for override first
  if (FEATURED_OVERRIDES[lane]) {
    item = FEATURED_OVERRIDES[lane];
  } else {
    // Otherwise use the indexed card from editorial content
    const content = EDITORIAL_CONTENT_BY_LANE[lane];
    const index = FEATURED_CARD_INDEX[lane] ?? 0;
    item = content?.cards[index];
  }

  // Validate that we have both image and label before returning
  if (!item?.image || !item?.label) {
    return undefined;
  }

  return item;
}

/**
 * Check if query already contains gender-specific terms.
 * Used to avoid redundant suffixes like "Kid's boots for kids".
 */
export function queryHasGenderContext(query: string): boolean {
  const lowerQuery = query.toLowerCase();
  return (
    lowerQuery.includes("women's") ||
    lowerQuery.includes("women") ||
    lowerQuery.includes("men's") ||
    lowerQuery.includes("men") ||
    lowerQuery.includes("kid's") ||
    lowerQuery.includes("kids") ||
    lowerQuery.includes("unisex")
  );
}

/**
 * Map lane to gender context string.
 */
export function laneToGenderContext(lane?: EditorialLane): string {
  switch (lane) {
    case EDITORIAL_LANE.WOMENSWEAR:
      return "Women's";
    case EDITORIAL_LANE.MENSWEAR:
      return "Men's";
    case EDITORIAL_LANE.KIDSWEAR:
      return "Kids";
    case EDITORIAL_LANE.BEAUTY:
      return "Beauty";
    default:
      return "";
  }
}

/**
 * Build chat data for a featured editorial search.
 * Handles gender context (avoiding redundant suffixes) and category tokens.
 */
export function buildFeaturedSearchChatData(
  lane: string,
  label: string,
  userId?: string
): {
  query: string;
  originalQuery: string;
  gender: string | undefined;
  category: string | undefined;
  userId: string | undefined;
  timestamp: number;
} {
  const normalizedLane = isEditorialLane(lane) ? lane : undefined;
  const genderContext = laneToGenderContext(normalizedLane);
  // Use shared editorialCategoryToToken from genderMapping.ts (supports Beauty)
  const categoryToken = editorialCategoryToToken(normalizedLane);

  // Only add gender suffix for apparel lanes (not Beauty) if query doesn't already contain gender context
  // Beauty is a category, not a demographic - "Clean beauty essentials" shouldn't become "Clean beauty essentials for beauty"
  let query = label;
  const isApparelLane =
    normalizedLane === EDITORIAL_LANE.WOMENSWEAR ||
    normalizedLane === EDITORIAL_LANE.MENSWEAR ||
    normalizedLane === EDITORIAL_LANE.KIDSWEAR;
  if (isApparelLane && genderContext && !queryHasGenderContext(label)) {
    const genderSuffix = genderContext.toLowerCase().replace("'s", "");
    query = `${label} for ${genderSuffix}`;
  }

  return {
    query,
    originalQuery: label,
    gender: genderContext || undefined,
    category: categoryToken,
    userId,
    timestamp: Date.now(),
  };
}
